import React from "react";
import {Box, Shimmer, Tiles, Text, VisuallyHidden} from "@unibuddy/patron";
import {useTranslation} from "@unibuddy/intl";
import {ApolloError} from "apollo-client";
import PageTitle from "shared/Shared/components/PageTitle/PageTitle";

import {
    RecommendationPageTitle,
    RecommendationPageSubtitle,
    RecommendationBannerText,
} from "../Recommendations";
import {
    GenerateRecommendationsMutation,
    useGenerateRecommendationsMutation,
} from "../../NextBestActions/NextBestActions";
import {
    CardContainer,
    RecommendationStarIcon,
} from "../../NextBestActions/containers/NextBestActions";
import NextBestActionsCard from "../../NextBestActions/components/NextBestActionsCard";
import useUniversityColor from "../../UniversityProvider/hooks/useUniversityColor";
import {useUniversity} from "../../UniversityProvider/UniversityProvider";

const BATCH_SIZE = 6;

type RecommendationsProps = {
    isLoading: boolean;
    error: ApolloError;
    data?: GenerateRecommendationsMutation;
};

const RecommendationPageHeader: React.FC = () => {
    const {t, ready} = useTranslation("decisionStage");
    const universityColor = useUniversityColor();

    if (!ready) return <p>Loading...</p>;

    return (
        <>
            <VisuallyHidden>
                <PageTitle
                    title={t(RecommendationPageTitle.translationKey, RecommendationPageTitle.name)}
                />
            </VisuallyHidden>
            <Box display="flex" flexDirection="column">
                <Box
                    display="flex"
                    alignItems="center"
                    height="42px"
                    borderRadius="xxsmall"
                    backgroundColor={universityColor}
                    width="100%"
                    paddingX="medium"
                >
                    <RecommendationStarIcon />
                    <Text
                        transform="uppercase"
                        color="#FFFFFF"
                        size="small"
                        lineHeight="20px"
                        weight="600"
                        truncate
                    >
                        {t(RecommendationBannerText.translationKey, RecommendationBannerText.name)}
                    </Text>
                </Box>
                <Box paddingY="medium">
                    <Text color="#424B5A" size="medium">
                        {t(
                            RecommendationPageSubtitle.translationKey,
                            RecommendationPageSubtitle.name,
                        )}
                    </Text>
                </Box>
            </Box>
        </>
    );
};

function createShimmers(count: number) {
    const shimmers: number[] = new Array(count).fill(Math.random());
    return shimmers.map(() => (
        <Box key={`idx:${Math.random()}`} margin="small">
            <Shimmer height="229px" width="100%">
                <rect x="0" y="0" rx={4} ry={4} width="100%" height="229px" />
            </Shimmer>
        </Box>
    ));
}

const RecommendationLoading: React.FC = () => (
    <Box width="100%" marginBottom="xsmall" data-test-id="recommendation-page-loading-state">
        <Tiles columns={[1, 2, 3]}>{createShimmers(BATCH_SIZE)}</Tiles>
    </Box>
);

const Recommendations: React.FC<RecommendationsProps> = (props) => {
    if (props.isLoading || !props.data) {
        return <RecommendationLoading />;
    } else if (props.error) {
        console.error(props.error);
        return (
            <Text data-test-id="recommendation-page-error-state" color="red">
                Failed to load recommendations
            </Text>
        );
    }

    return (
        <Tiles columns={[1, 2, 3]} space="medium">
            {props.data.generateRecommendations.map((content) => (
                <CardContainer key={content.id}>
                    <NextBestActionsCard
                        contentId={content.contentId}
                        title={content.title}
                        description={content.description}
                        contentType={content.type}
                        url={content.url}
                        batchId={content.batchId}
                    />
                </CardContainer>
            ))}
        </Tiles>
    );
};

export const RecommendationPage: React.ExoticComponent = React.memo(() => {
    const university = useUniversity();
    const [getGeneratedRecommendations, {data, loading, error}] =
        useGenerateRecommendationsMutation({
            variables: {
                universityId: university.id,
                batchSize: BATCH_SIZE,
            },
        });

    const getRecommendations = React.useCallback(async () => {
        try {
            await getGeneratedRecommendations();
        } catch (e) {
            console.error("Error generating NBA recommendations", e);
        }
    }, [getGeneratedRecommendations]);

    React.useEffect(() => {
        getRecommendations();
    }, [getRecommendations]);

    return (
        <Box marginBottom="medium">
            <RecommendationPageHeader />
            <Recommendations data={data} isLoading={loading} error={error} />
        </Box>
    );
});
